import {
  encryptForTransit,
  normalizeTransitCiphertextBase64,
  isOpenSslSaltedCiphertextBase64,
} from "../../utils/common";
import axiosInstance from "../api/axios";
import { base64ToFile } from "../../utils/fileUtils";

/**
 * For POST /projects/phase/combined: omit bank_details when empty; if already transit ciphertext, send padded base64;
 * if plaintext and REACT_APP_TRANSIT_ENC_KEY is set, encrypt; never send raw bank details without encryption.
 */
function appendBankDetailsForCombinedEndpoint(formData, projectData) {
  const raw = projectData.bank_details ?? projectData.bankDetails;
  if (raw == null) return;
  if (typeof raw === "object") return;
  const str = String(raw).trim();
  if (!str) return;

  if (isOpenSslSaltedCiphertextBase64(str)) {
    formData.append("bank_details", normalizeTransitCiphertextBase64(str));
    return;
  }

  const encrypted = encryptForTransit(str);
  if (!encrypted || encrypted === str) {
    return;
  }
  formData.append("bank_details", encrypted);
}

// Fetch all projects
export const fetchProjects = async (params) => {
  try {
    const response = await axiosInstance.get("/projects", {
      params: params,
    });

    // Handle both paginated and non-paginated responses for backward compatibility
    let projects = [];
    if (response?.items && response.total !== undefined) {
      // Paginated response - return items array
      projects = response.items;
    } else if (Array.isArray(response)) {
      // Already an array
      projects = response;
    }
    
    return projects;
  } catch (error) {
    console.error("Error fetching projects:", error.response || error);
    throw error;
  }
};

// Fetch project phases data
export const fetchProjectPhases = async (params) => {
  try {
    const response = await axiosInstance.get("/projects/phase", {
      params: params,
    });
    return response;
  } catch (error) {
    console.error("Error fetching project phases:", error.response || error);
    throw error;
  }
};

// Fetch project phase media (images, progress, guidelines, boq)
export const fetchProjectPhaseMedia = async (projectPhaseId, options = {}) => {
  try {
    const params = {
      projectPhaseId: projectPhaseId,
      images: options.images !== undefined ? options.images : false,
      progress: options.progress !== undefined ? options.progress : false,
      guidelines: options.guidelines !== undefined ? options.guidelines : false,
      boq: options.boq !== undefined ? options.boq : false,
    };
    
    const response = await axiosInstance.get("/projects/phase/media", {
      params: params,
    });
    
    // Handle different response formats
    let mediaData = [];
    if (response?.data) {
      // Axios wraps response in data
      if (response.data?.items && Array.isArray(response.data.items)) {
        mediaData = response.data.items;
      } else if (Array.isArray(response.data)) {
        mediaData = response.data;
      }
    } else if (response?.items && Array.isArray(response.items)) {
      mediaData = response.items;
    } else if (Array.isArray(response)) {
      mediaData = response;
    }
    
    return mediaData;
  } catch (error) {
    console.error("Error fetching project phase media:", error.response || error);
    throw error;
  }
};
export const fetchPropertyConfigurations = async (params) => {
  try {
    const response = await axiosInstance.get(`/property/config`, { params });
    return response;
  } catch (error) {
    console.error("Error fetching property configurations:", error);
    throw error;
  }
};


export const updatePropertyData = async (editedProperty) => {
  try {
    const { propertyId, ...updatedFields } = editedProperty;

    const response = await axiosInstance.put(`/property`, updatedFields, {
      params: { propertyId },
    });

    return response;
  } catch (error) {
    console.error("Error updating property data:", error);
    throw error;
  }
};

// Fetch property data by projectId
export const fetchPropertyData = async (projectId, customerId, phaseId, search, searchField) => {
  try {
    const params = {
      projectId: projectId,
      customerId: customerId,
      projectPhaseId: phaseId
    };
    
    // Add search parameters if provided
    if (search) {
      params.search = search;
      if (searchField) {
        params.search_field = searchField;
      }
    }
    
    const response = await axiosInstance.get(`/property`, {
      params: params,
    });
    return response;
  } catch (error) {
    console.error("Error fetching property data:", error);
    throw error;
  }
};

// Fetch all properties
export const fetchAllProperties = async (customerId, loginId) => {
  try {
    const params = {};

    if (customerId) params.customerId = customerId;
    if (loginId) params.loginId = loginId;

    const response = await axiosInstance.get("/property", { params });
    return response;
  } catch (error) {
    console.error("Error fetching all properties:", error);
    throw error;
  }
};




export const getPropertyById = async (propertyId) => {
  try {
    const response = await axiosInstance.get(`/property`, {
      params: { propertyId: propertyId }, // Pass propertyId as a query parameter
    });
    return response;
  } catch (error) {
    console.error("Error fetching property by ID:", error);
    throw error;
  }
};
// Create a new project
export const createProject = async (projectData) => {
  try {
    const response = await axiosInstance({
      method: "POST",
      url: "/projects/phase/combined",
      data: projectData,
      headers: { "Content-Type": "multipart/form-data" },
    });

    console.log("API Response in createProject service:", response);
    return response;
  } catch (error) {
    console.error("Error creating project:", error);
    throw error;
  }
};

// Edit an existing project
export const editProject = async (projectId, projectData, loginId) => {
  try {
    const url = `/projects?projectId=${projectId}&loginId=${loginId}`;
    const response = await axiosInstance({
      method: "PUT",
      url: url,
      data: projectData,
      headers: { "Content-Type": "multipart/form-data" },
    });

    console.log("API Response in editProject service:", response);
    return response;
  } catch (error) {
    console.error("Error editing project:", error);
    throw error;
  }
};
// Fetch property metadata by propertyId
export const fetchPropertyMetadata = async (propertyId) => {
  try {
    const response = await axiosInstance.get(`/property/metadata`, {
      params: { propertyId },
    });
    return response;
  } catch (error) {
    console.error("Error fetching property metadata:", error);
    throw error;
  }
};

export const getProjectDeatilaFormdata = (projectDeatilaData) => {
  console.log("=== getProjectDeatilaFormdata called ===");
  console.log("Input data to service:", projectDeatilaData);
  console.log("Call stack:", new Error().stack);

  // Add a timestamp to prevent duplicate processing
  const timestamp = Date.now();
  console.log("Processing timestamp:", timestamp);

  const formDataToSend = new FormData();

  const isCamelCase = projectDeatilaData.projectName !== undefined;
  const d = projectDeatilaData;
  const nested = d.address;
  // Support nested `address`, flat camelCase on root (e.g. ProjectDetailsContent edit), or snake_case keys (create flow).
  const address = {
    buildingNo:
      nested?.buildingNo ?? d.buildingNo ?? d.address_building_no ?? "",
    streetName1:
      nested?.streetName1 ?? d.streetName1 ?? d.address_street_name1 ?? "",
    streetName2:
      nested?.streetName2 ?? d.streetName2 ?? d.address_street_name2 ?? "",
    city: nested?.city ?? d.city ?? d.address_city ?? "",
    district: nested?.district ?? d.district ?? d.address_district ?? "",
    pinCode: nested?.pinCode ?? d.pinCode ?? d.address_pin_code ?? "",
    state: nested?.state ?? d.state ?? d.address_state ?? "",
    country: nested?.country ?? d.country ?? d.address_country ?? "",
  };

  // Project details
  formDataToSend.append(
    "project_name",
    isCamelCase
      ? projectDeatilaData.projectName
      : projectDeatilaData.project_name
  );
  formDataToSend.append("developer", projectDeatilaData.developer);
  formDataToSend.append(
    "customer_id",
    projectDeatilaData.customerId || projectDeatilaData.customer_id || 0
  );
  formDataToSend.append("address_building_no", address?.buildingNo || "");
  formDataToSend.append("address_street_name1", address?.streetName1 || "");
  formDataToSend.append("address_street_name2", address?.streetName2 || "");
  formDataToSend.append("address_city", address?.city || "");
  formDataToSend.append("address_district", address?.district || "");
  formDataToSend.append("address_pin_code", address?.pinCode || "");
  formDataToSend.append("address_state", address?.state || "");
  formDataToSend.append("address_country", address?.country || "");
  formDataToSend.append("amenities", projectDeatilaData.amenities);
  formDataToSend.append("towers", projectDeatilaData.project_towers || projectDeatilaData.projectTowers || projectDeatilaData.towers);
  formDataToSend.append("units", projectDeatilaData.project_units || projectDeatilaData.projectUnits || projectDeatilaData.units);
  formDataToSend.append(
    "phone_number",
    projectDeatilaData.phoneNumber || projectDeatilaData.phone_number || ""
  );
  formDataToSend.append(
    "whatsapp_number",
    projectDeatilaData.whatsappNumber || projectDeatilaData.whatsapp_number || ""
  );
  formDataToSend.append("specifications", projectDeatilaData.specifications);
  formDataToSend.append("location", projectDeatilaData.location);
  formDataToSend.append(
    "size_acres",
    parseFloat(projectDeatilaData.size_acres || projectDeatilaData.sizeAcres)
  );
  appendBankDetailsForCombinedEndpoint(formDataToSend, projectDeatilaData);

  // Handle project images
  if (projectDeatilaData.images) {
    projectDeatilaData.images.forEach((image) => {
      if (image && image.base64) {
        try {
          formDataToSend.append(
            "project_images",
            base64ToFile(image.base64, image.name)
          );
        } catch (error) {
          console.error("Error appending image", error);
        }
      }
    });
  }

  // Handle marketing media
  if (projectDeatilaData.marketing_media) {
    projectDeatilaData.marketing_media.forEach((media) => {
      if (media && media.base64) {
        try {
          formDataToSend.append(
            "marketing_media",
            base64ToFile(media.base64, media.name)
          );
        } catch (error) {
          console.error("Error appending marketing media", error);
        }
      }
    });
  }

  // Handle phases data - convert to array of strings
  console.log("Processing phases:", projectDeatilaData.phases);
  if (projectDeatilaData.phases && Array.isArray(projectDeatilaData.phases)) {
    console.log(`Processing ${projectDeatilaData.phases.length} phases`);

    // Arrays to collect files from all phases
    const interiorDesignGuidelinesFiles = [];
    const reraBoqDocumentsFiles = [];

    projectDeatilaData.phases.forEach((phase, index) => {
      console.log(`Processing phase ${index + 1}:`, phase);
      
      // Create phase data object - only include fields that have values
      const phaseData = {
        name: phase.name || "",
        towers: parseInt(phase.phase_towers || phase.phaseTowers || phase.towers || 0),
        units: parseInt(phase.phase_units || phase.phaseUnits || phase.units || 0),
      };

      // Add optional fields only if they have values
      if (phase.rera_no || phase.phaseReraNo) {
        phaseData.rera_no = phase.rera_no || phase.phaseReraNo;
      }
      if (phase.rera_start_date || phase.reraStartDate) {
        phaseData.rera_start_date = phase.rera_start_date || phase.reraStartDate;
      }
      if (phase.rera_end_date || phase.phaseReraEndDate) {
        phaseData.rera_end_date = phase.rera_end_date || phase.phaseReraEndDate;
      }
      if (phase.phase_status !== undefined || phase.phaseStatus !== undefined) {
        phaseData.phase_status = phase.phase_status !== undefined ? phase.phase_status : phase.phaseStatus;
      }

      // Append phase data as JSON string to project_phase_data array
      formDataToSend.append("project_phase_data", JSON.stringify(phaseData));

      // Handle phase images - append each image for this phase with indexed notation
      // Format: project_phase_images[phaseIndex][imageIndex]
      // This creates an array of arrays structure
      if (phase.images && Array.isArray(phase.images) && phase.images.length > 0) {
        phase.images.forEach((image, imgIndex) => {
          if (image && image.base64) {
            try {
              formDataToSend.append(
                `project_phase_images[${index}][${imgIndex}]`,
                base64ToFile(image.base64, image.name)
              );
            } catch (error) {
              console.error(`Error appending phase ${index + 1} image ${imgIndex + 1}:`, error);
            }
          }
        });
      }

      // Collect interior design guidelines files from all phases
      if (phase.interior_design_guidelines || phase.interiorDesignGuidelines) {
        const guidelinesFile = phase.interior_design_guidelines || phase.interiorDesignGuidelines;
        if (guidelinesFile && guidelinesFile.base64) {
          interiorDesignGuidelinesFiles.push(guidelinesFile);
        }
      }

      // Collect RERA BOQ document files from all phases
      if (phase.rera_boq_document || phase.reraBoqDocument) {
        const boqFile = phase.rera_boq_document || phase.reraBoqDocument;
        if (boqFile && boqFile.base64) {
          reraBoqDocumentsFiles.push(boqFile);
        }
      }
    });

    // Append all interior design guidelines files as array
    interiorDesignGuidelinesFiles.forEach((file) => {
      try {
        formDataToSend.append(
          "interior_design_guidelines",
          base64ToFile(file.base64, file.name)
        );
      } catch (error) {
        console.error("Error appending interior design guidelines:", error);
      }
    });

    // Append all RERA BOQ document files as array
    reraBoqDocumentsFiles.forEach((file) => {
      try {
        formDataToSend.append(
          "rera_boq_documents",
          base64ToFile(file.base64, file.name)
        );
      } catch (error) {
        console.error("Error appending RERA BOQ document:", error);
      }
    });
  }

  console.log("FormData entries:");
  let phaseDataCount = 0;
  let phaseImagesCount = 0;
  let interiorGuidelinesCount = 0;
  let reraBoqCount = 0;
  for (let [key, value] of formDataToSend.entries()) {
    if (key === "project_phase_data") {
      phaseDataCount++;
      console.log(key, typeof value === 'object' ? '[File]' : value);
    } else if (key.startsWith("project_phase_images")) {
      phaseImagesCount++;
      console.log(key, '[File]');
    } else if (key === "interior_design_guidelines") {
      interiorGuidelinesCount++;
      console.log(key, '[File]');
    } else if (key === "rera_boq_documents") {
      reraBoqCount++;
      console.log(key, '[File]');
    } else {
      console.log(key, typeof value === 'object' ? '[File]' : value);
    }
  }
  console.log(`Total project_phase_data entries: ${phaseDataCount}`);
  console.log(`Total project_phase_images entries: ${phaseImagesCount}`);
  console.log(`Total interior_design_guidelines entries: ${interiorGuidelinesCount}`);
  console.log(`Total rera_boq_documents entries: ${reraBoqCount}`);

  

  return formDataToSend;
};

export const getPropertyConfigFormdata = (propertyConfig) => {
  const formData = new FormData();
  const isCamelCase = propertyConfig.configName !== undefined;
  formData.append(
    "project_id",
    isCamelCase
      ? propertyConfig.projectId
      : propertyConfig.project_id
  );
  formData.append(
    "config_name",
    isCamelCase ? propertyConfig.configName : propertyConfig.config_name
  );
  formData.append(
    "size_bhk",
    isCamelCase ? propertyConfig.sizeBhk : propertyConfig.size_bhk
  );
  formData.append(
    "property_type",
    isCamelCase ? propertyConfig.propertyType : propertyConfig.property_type
  );
  formData.append(
    "carpet_area_sqmt",
    isCamelCase ? propertyConfig.carpetAreaSqmt : propertyConfig.carpet_area_sqmt
  );
  formData.append(
    "carpet_area_sqft",
    isCamelCase ? propertyConfig.carpetAreaSqft : propertyConfig.carpet_area_sqft
  );
  formData.append(
    "super_builtup_area_sqmt",
    isCamelCase
      ? propertyConfig.superBuiltupAreaSqmt
      : propertyConfig.super_builtup_area_sqmt
  );
  formData.append(
    "super_builtup_area_sqft",
    isCamelCase
      ? propertyConfig.superBuiltupAreaSqft
      : propertyConfig.super_builtup_area_sqft
  );
  formData.append(
    "dry_balcony_area_sqft",
    isCamelCase
      ? propertyConfig.dryBalconyAreaSqft
        ? parseFloat(propertyConfig.dryBalconyAreaSqft)
        : 0
      : propertyConfig.dry_balcony_area_sqft
        ? parseFloat(propertyConfig.dry_balcony_area_sqft)
        : 0
  );
  formData.append(
    "dry_balcony_area_sqmt",
    isCamelCase
      ? propertyConfig.dryBalconyAreaSqmt
        ? parseFloat(propertyConfig.dryBalconyAreaSqmt)
        : 0
      : propertyConfig.dry_balcony_area_sqmt
        ? parseFloat(propertyConfig.dry_balcony_area_sqmt)
        : 0
  );
  formData.append(
    "balcony_area_sqmt",
    isCamelCase
      ? propertyConfig.balconyAreaSqmt
        ? parseFloat(propertyConfig.balconyAreaSqmt)
        : 0
      : propertyConfig.balcony_area_sqmt
        ? parseFloat(propertyConfig.balcony_area_sqmt)
        : 0
  );
  formData.append(
    "balcony_area_sqft",
    isCamelCase
      ? propertyConfig.balconyAreaSqft
        ? parseFloat(propertyConfig.balconyAreaSqft)
        : 0
      : propertyConfig.balcony_area_sqft
        ? parseFloat(propertyConfig.balcony_area_sqft)
        : 0
  );
  formData.append(
    "base_price",
    isCamelCase ? propertyConfig.basePrice : propertyConfig.base_price
  );
  formData.append(
    "currency_id",
    isCamelCase ? propertyConfig.currencyId : propertyConfig.currency_id
  );

  const images = isCamelCase
    ? propertyConfig.propertyConfigImages
    : propertyConfig.property_config_images;
  if (images) {
    images.forEach((image) => {
      formData.append(
        "property_config_images",
        base64ToFile(image.base64, image.name)
      );
    });
  }
  return formData;
};
// Example inside services/projects.js
export const fetchAssignedConfigs = async (projectId, buildingId, phaseId) => {
  try {
    const response = await axiosInstance.get("/building/property/config", {
      params: {
        project_id: projectId,
        buildingIds: buildingId.toString(),
        projectPhaseId: phaseId
      }
    });
    return response;
  } catch (error) {
    console.error(`Error fetching assigned configurations for building ${buildingId}:`, error);
    return null;
  }
};